'use client';

import { getUploadMetadata } from '@/app/apis';
import type { ReferencePrompt, SourceAudio } from '@/app/types';
import { getAudioMetadata } from '@/app/utils/client';

const probedAudioDurations = new Map<string, number>();

const getRemoteAudioDuration = async (audioUrl: string): Promise<number | undefined> => {
  if (!/^https?:\/\//i.test(audioUrl)) return undefined;

  try {
    const result = await getUploadMetadata({ query: { uris: [audioUrl] } });
    const duration = result.data?.metadata?.[0]?.duration;
    return typeof duration === 'number' && Number.isFinite(duration) && duration > 0 ? duration : undefined;
  } catch {
    return undefined;
  }
};

export const isReferenceAudioDurationValid = (duration: number, minDuration: number, maxDuration: number) =>
  Number.isFinite(duration) && duration >= minDuration && duration <= maxDuration;

export const resolveReferenceAudioDuration = async (audio: SourceAudio): Promise<number | undefined> => {
  if (typeof audio.duration === 'number' && Number.isFinite(audio.duration) && audio.duration > 0) {
    return audio.duration;
  }

  const audioUrl = audio.src || audio.preview;
  if (!audioUrl) return undefined;

  const cached = probedAudioDurations.get(audioUrl);
  if (cached !== undefined) return cached;

  const remoteDuration = await getRemoteAudioDuration(audioUrl);
  if (remoteDuration !== undefined) {
    probedAudioDurations.set(audioUrl, remoteDuration);
    return remoteDuration;
  }

  try {
    const { duration } = await getAudioMetadata(audioUrl);
    if (Number.isFinite(duration) && duration > 0) {
      probedAudioDurations.set(audioUrl, duration);
      return duration;
    }
  } catch {
    // The caller reports an actionable error and keeps invalid audio out of the request.
  }

  return undefined;
};

export type ReferenceAudioDurationValidation =
  | { ok: true; resolved: Array<{ referenceId: string; duration: number }> }
  | { ok: false; reason: 'unknownDuration'; referenceId: string }
  | { ok: false; reason: 'outOfRange'; referenceId: string; duration: number };

export const validateReferenceAudioDurations = async (
  referencePrompts: ReferencePrompt[],
  minDuration: number,
  maxDuration: number,
): Promise<ReferenceAudioDurationValidation> => {
  const audioReferences = referencePrompts.filter(
    (reference): reference is Extract<ReferencePrompt, { type: 'audio' }> =>
      reference.type === 'audio' && reference.status === 'done' && !!reference.prompt,
  );
  const durations = await Promise.all(
    audioReferences.map((reference) => resolveReferenceAudioDuration(reference.prompt as SourceAudio)),
  );
  const resolved: Array<{ referenceId: string; duration: number }> = [];

  for (const [index, reference] of audioReferences.entries()) {
    const duration = durations[index];
    if (duration === undefined) {
      return { ok: false, reason: 'unknownDuration', referenceId: reference.id };
    }
    if (!isReferenceAudioDurationValid(duration, minDuration, maxDuration)) {
      return { ok: false, reason: 'outOfRange', referenceId: reference.id, duration };
    }
    resolved.push({ referenceId: reference.id, duration });
  }

  return { ok: true, resolved };
};
